import React, { useEffect, useState, useRef } from "react";
import io from "socket.io-client";

const SOCKET_URL = "https://tradespots.online";

const AdminContact = () => {
  const [loading, setLoading] = useState(true);
  const [userGroups, setUserGroups] = useState({}); // { userId: { username, messages: [] } }
  const [selectedUser, setSelectedUser] = useState(null);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState("");
  const socketRef = useRef();

  // Fetch all messages grouped by user
  useEffect(() => {
    setLoading(true);
    fetch("https://tradespots.online/api/contact-messages/admin/all")
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setUserGroups(data.data);
        setLoading(false);
      });
  }, []);

  // Socket.io: join admins room and listen for new user messages
  useEffect(() => {
    socketRef.current = io(SOCKET_URL, { transports: ["websocket"] });
    socketRef.current.emit("join", { isAdmin: true });
    socketRef.current.on("newUserMessage", (data) => {
      // Update userGroups with new message
      setUserGroups((prev) => {
        const userId = data.userId;
        const username = data.username || userId;
        const msg = {
          _id: Date.now() + Math.random(),
          from: "user",
          message: data.message,
          createdAt: new Date().toISOString(),
        };
        return {
          ...prev,
          [userId]: {
            username,
            messages: prev[userId]?.messages ? [...prev[userId].messages, msg] : [msg],
          },
        };
      });
    });
    socketRef.current.on("newAdminReply", (data) => {
      // Echo admin's own reply in real time
      setUserGroups((prev) => {
        const userId = data.userId;
        const msg = {
          _id: Date.now() + Math.random(),
          from: "admin",
          message: data.message,
          createdAt: new Date().toISOString(),
        };
        return {
          ...prev,
          [userId]: {
            ...prev[userId],
            messages: prev[userId]?.messages ? [...prev[userId].messages, msg] : [msg],
          },
        };
      });
    });
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  // Send admin reply (emit via socket and save to backend)
  const handleReply = async () => {
    if (!reply.trim() || !selectedUser) return;
    setStatus("");
    // Emit reply to user in real time
    if (socketRef.current) {
      socketRef.current.emit("adminReply", { userId: selectedUser, message: reply });
    }
    try {
      const res = await fetch("https://tradespots.online/api/contact-messages/admin/reply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: selectedUser, message: reply }),
      });
      const data = await res.json();
      if (data.success) {
        // Optimistically update UI immediately
        setUserGroups((prev) => {
          const msg = {
            _id: Date.now() + Math.random(),
            from: "admin",
            message: reply,
            createdAt: new Date().toISOString(),
          };
          return {
            ...prev,
            [selectedUser]: {
              ...prev[selectedUser],
              messages: prev[selectedUser]?.messages ? [...prev[selectedUser].messages, msg] : [msg],
            },
          };
        });
        setReply("");
        setStatus("Reply sent!");
        // No setRefresh here; rely on socket to update UI
      } else {
        setStatus(data.error || "Failed to send reply");
      }
    } catch {
      setStatus("Failed to send reply");
    }
  };

  return (
    <div
      style={{
        padding: 0,
        maxWidth: 900,
        margin: "0 auto",
        fontFamily: "'Poppins', Arial, sans-serif",
        background: "#101510",
        minHeight: "100vh",
        color: "#f1f1f1",
      }}
    >
      {/* Back Button */}
      <button
        onClick={() => window.history.back()}
        style={{
          background: '#23272f',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '10px 22px',
          fontWeight: 700,
          fontSize: 16,
          cursor: 'pointer',
          margin: '18px 0 18px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'background 0.2s',
          display: 'inline-block',
        }}
      >
        ← Back
      </button>
      {/* Gradient Header */}
      <div
        style={{
          background: "linear-gradient(90deg, #0f4c0c 0%, #bfa100 100%)",
          borderRadius: "0 0 18px 18px",
          padding: "28px 0 18px 0",
          marginBottom: 24,
          boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
          textAlign: "center",
        }}
      >
        <h1 style={{ color: "#fff", fontWeight: 700, fontSize: 38, margin: 0, textShadow: "2px 2px 8px #23272f" }}>
          Admin User Messages
        </h1>
        <div style={{ color: "#fff", fontSize: 18, marginTop: 8, fontWeight: 400, textShadow: "1px 1px 6px #23272f" }}>
          View and reply to user messages here.
        </div>
      </div>
      {/* Card Container */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 32,
          background: "#181f2a",
          borderRadius: 18,
          boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
          padding: 24,
          margin: "0 auto",
          maxWidth: 900,
          flexWrap: "wrap",
        }}
      >
        {/* User list */}
        <div
          style={{
            minWidth: 220,
            flex: "1 1 220px",
            background: "linear-gradient(90deg, #0f4c0c 0%, #bfa100 100%)",
            borderRadius: 14,
            padding: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            marginBottom: 16,
            maxHeight: 340, // fixed height for scroll
            overflowY: "auto", // make scrollable
          }}
        >
          <h4 style={{ color: "#fff", marginBottom: 12, fontWeight: 700, fontSize: 20, letterSpacing: 1 }}>Users</h4>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {Object.entries(userGroups).map(([userId, group]) => (
              <li key={userId} style={{ margin: "8px 0" }}>
                <button
                  style={{
                    background: selectedUser === userId ? "#1b5e20" : "rgba(0,0,0,0.18)",
                    color: selectedUser === userId ? "#fff" : "#fff",
                    border: selectedUser === userId ? "2px solid #bfa100" : "1px solid #333",
                    borderRadius: 8,
                    padding: "10px 14px",
                    cursor: "pointer",
                    width: "100%",
                    textAlign: "left",
                    fontWeight: 600,
                    fontSize: 15,
                    transition: "background 0.2s, border 0.2s",
                    outline: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                  onClick={() => setSelectedUser(userId)}
                >
                  {/* Only show email and SPOTID, no userId or username */}
                  {group.email && (
                    <span style={{ fontWeight: 700, fontSize: 15 }}>
                      {group.email}
                    </span>
                  )}
                  {group.messages && group.messages[0]?.spotId && (
                    <span style={{ color: '#bfa100', fontSize: 12, marginLeft: 0 }}>
                      (SPOTID: {group.messages[0].spotId})
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
        {/* Messages and reply */}
        <div
          style={{
            flex: "2 1 320px",
            minWidth: 0,
            background: "#181f2a",
            borderRadius: 14,
            padding: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            marginBottom: 16,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <div style={{ textAlign: "center", color: "#bfc3c9" }}>Loading...</div>
          ) : selectedUser ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 style={{ color: "#bfa100", margin: 0, fontWeight: 700, fontSize: 20 }}>
                  Messages with {userGroups[selectedUser]?.username || selectedUser}
                </h4>
                <button
                  onClick={() => setSelectedUser(null)}
                  style={{
                    background: 'transparent',
                    color: '#e53935',
                    border: 'none',
                    fontSize: 28,
                    fontWeight: 700,
                    cursor: 'pointer',
                    marginLeft: 4
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  maxHeight: 350,
                  overflowY: "auto",
                  border: "1px solid #333",
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 16,
                  background: "#101510",
                }}
              >
                {userGroups[selectedUser]?.messages.map((msg) => (
                  <div
                    key={msg._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: msg.from === "admin" ? "flex-end" : "flex-start",
                      margin: "8px 0",
                    }}
                  >
                    <div
                      style={{
                        background: msg.from === "admin" ? "linear-gradient(90deg, #0f4c0c 0%, #bfa100 100%)" : "#23272f",
                        color: msg.from === "admin" ? "#fff" : "#bfc3c9",
                        borderRadius: 8,
                        padding: "8px 14px",
                        maxWidth: "90%",
                        fontSize: 15,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        flex: 1,
                        wordBreak: 'break-word',
                      }}
                    >
                      {msg.message}
                      <div style={{ fontSize: 12, color: "#bfa100", marginTop: 4 }}>{new Date(msg.createdAt).toLocaleString()}</div>
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm("Delete this message?")) return;
                        try {
                          const res = await fetch(`https://tradespots.online/api/contact-messages/admin/message/${msg._id}`, {
                            method: 'DELETE',
                          });
                          const data = await res.json();
                          if (data.success) {
                            setUserGroups(prev => {
                              const updated = { ...prev };
                              updated[selectedUser].messages = updated[selectedUser].messages.filter(m => m._id !== msg._id);
                              return updated;
                            });
                          } else {
                            alert(data.error || 'Failed to delete message');
                          }
                        } catch {
                          alert('Failed to delete message');
                        }
                      }}
                      style={{
                        marginLeft: 10,
                        background: '#e53935',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '2px 10px',
                        fontSize: 13,
                        cursor: 'pointer',
                        height: 'fit-content',
                        alignSelf: 'flex-start',
                      }}
                      title="Delete this message"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <input
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Type your reply..."
                  style={{
                    flex: 1,
                    borderRadius: 8,
                    border: "1px solid #bfa100",
                    padding: 12,
                    fontSize: 16,
                    background: "#101510",
                    color: "#fff",
                    outline: "none",
                  }}
                />
                <button
                  onClick={handleReply}
                  style={{
                    background: "linear-gradient(90deg, #0f4c0c 0%, #bfa100 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px 22px",
                    fontWeight: 700,
                    fontSize: 16,
                    cursor: "pointer",
                    transition: "background 0.2s",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  Send
                </button>
              </div>
              {status && (
                <div
                  style={{
                    color: status.includes("sent") ? "#43a047" : "#e53935",
                    marginTop: 8,
                    fontWeight: 600,
                  }}
                >
                  {status}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: "#bfc3c9", textAlign: "center", marginTop: 40 }}>
              Select a user to view messages and reply.
            </div>
          )}
        </div>
      </div>
      {/* Responsive styles */}
      <style>{`
        @media (max-width: 700px) {
          div[style*='display: flex'][style*='gap: 32px'] {
            flex-direction: column !important;
            gap: 0 !important;
            padding: 0 !important;
          }
          div[style*='min-width: 220px'] {
            min-width: 0 !important;
            width: 100% !important;
            margin-bottom: 12px !important;
          }
          div[style*='flex: 2 1 320px'] {
            min-width: 0 !important;
            width: 100% !important;
            margin-bottom: 0 !important;
          }
        }
        @media (max-width: 500px) {
          h1 {
            font-size: 1.3rem !important;
          }
          h4 {
            font-size: 1rem !important;
          }
          input, button {
            font-size: 14px !important;
            padding: 8px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminContact;
